<template>
  <div :class="height">
    <VChart ref="chartRef" class="h-full w-full" :option="chartOptions" :theme="currentTheme" :key="chartKey" autoresize />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { usePropsStore } from "@/stores/props";
import VChart from "vue-echarts";
import { type ECElementEvent } from "echarts";

const propsStore = usePropsStore();
const currentTheme = computed<"customLight" | "customDark">(() => (propsStore.darkMode ? "customDark" : "customLight"));
const chartKey = ref(0); //Used to force chart refresh on theme update

interface Props {
  options: any;
  height?: string;
}

const props = withDefaults(defineProps<Props>(), {
  height: "h-[400px]",
});

const emit = defineEmits<{
  dataPointClick: [params: ECElementEvent];
}>();

const refreshChart = (): void => {
  chartKey.value++;
};

defineExpose({ refreshChart });

const chartRef = ref();
const chartOptions = ref(props.options);

watch(
  () => props.options,
  (newOptions) => {
    chartOptions.value = newOptions;
    chartKey.value++;
  },
  { deep: true }
);

watch(
  () => propsStore.theme,
  (theme) => {
    chartKey.value++;
  }
);

const handleChartClick = (params: ECElementEvent): void => {
  emit("dataPointClick", params);
};

const addClickListener = () => {
  const chartInstance = chartRef.value?.chart;
  if (chartInstance) {
    chartInstance.off("click", handleChartClick);
    chartInstance.on("click", handleChartClick);
  }
};

const removeClickListener = () => {
  const chartInstance = chartRef.value?.chart;
  if (chartInstance) {
    chartInstance.off("click", handleChartClick);
  }
};

watch(
  chartKey,
  () => {
    nextTick(() => addClickListener());
  },
  { immediate: true }
);

onUnmounted(() => {
  removeClickListener();
});
</script>
